import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AppConfigService, GridBaseQuery, GridHelperService } from '@core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CaseFormResponseDto } from '../dtos/case-form-response.dto';
import { FormVersionDetailResponseDto } from '../dtos/form-version-detail-response.dto';
import { FormVersionsListResponseDto } from '../dtos/form-versions-list-response.dto';
import { UpdateCaseFormRequestDto } from '../dtos/update-case-form-request.dto';
import { AddFieldRequestDto } from '../dtos/add-field-request.dto';
import { UpdateFieldRequestDto } from '../dtos/update-field-request.dto';
import { ReorderFieldsRequestDto } from '../dtos/reorder-fields-request.dto';
import { FieldDefinitionLibraryResponseDto } from '../dtos/field-definition-library-response.dto';
import { FieldTypeResponseDto } from '../dtos/field-constraint-type-response.dto';
import { CreateCaseFormRequestDto } from '../dtos/create-case-form-request.dto';
import { CreateCaseFormResponseDto } from '../dtos/create-case-form-response.dto';
import { SensitivityCategoryResponseDto } from '../dtos/sensitivity-category-response.dto';
import { KendoDataSourceResultDto } from '@shared';

/**
 * Kendo DataSource Result
 */
interface KendoDataSourceResult<T> {
  data: T[];
  total: number;
}

/**
 * Case Form API Service
 * Handles HTTP requests for case forms
 */
@Injectable({ providedIn: 'root' })
export class CaseFormApi {
  private readonly _http = inject(HttpClient);
  private readonly _config = inject(AppConfigService);
  private readonly _gridHelper = inject(GridHelperService);
  private readonly _baseUrl = `${this._config.versionedApiUrl}/cases/forms`;

  /**
   * Gets all case forms
   * GET /api/v1/cases/forms
   */
  public getAllCaseForms(): Observable<CaseFormResponseDto[]> {
    return this._http.get<KendoDataSourceResult<CaseFormResponseDto>>(this._baseUrl).pipe(
      map((result) => result.data)
    );
  }

  /**
   * Gets case forms with pagination
   * GET /api/v1/cases/forms with pagination params
   */
  public getCaseFormsWithPaging(query: GridBaseQuery): Observable<KendoDataSourceResultDto<CaseFormResponseDto>> {
    const params = this._gridHelper.buildGridParams(query);
    return this._http.get<KendoDataSourceResult<CaseFormResponseDto>>(this._baseUrl, { params }).pipe(
      map((result) => ({
        data: result.data,
        totalElements: result.total,
        totalPages: 0,
      })),
    );
  }

  /**
   * Gets case forms filtered by case type ID using Kendo filter
   * GET /api/v1/cases/forms with Filter query param
   * @param caseTypeId The case type ID to filter by
   * @returns Observable of case forms matching the case type
   */
  public getCaseFormsByCaseTypeId(caseTypeId: string): Observable<CaseFormResponseDto[]> {
    const params = new HttpParams()
      .set('Filter', `((caseTypeId~eq~${caseTypeId}))`);

    return this._http.get<KendoDataSourceResult<CaseFormResponseDto>>(this._baseUrl, { params }).pipe(
      map((result) => result.data)
    );
  }

  /**
   * Gets a single case form by case type ID
   * GET /api/v1/cases/forms with Filter query param
   * Since the relationship between case type and form is 1:1, returns the first match
   * @param caseTypeId The case type ID
   * @returns Observable of the case form or undefined if not found
   */
  public getCaseFormByCaseTypeId(caseTypeId: string): Observable<CaseFormResponseDto | undefined> {
    return this._http.get<CaseFormResponseDto>(`${this._baseUrl}/by-case-type/${caseTypeId}`).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) return of(undefined);
        throw error;
      })
    );
  }

  /**
   * Creates a new case form
   * POST /api/v1/cases/forms
   */
  public createCaseForm(dto: CreateCaseFormRequestDto): Observable<CreateCaseFormResponseDto> {
    return this._http.post<CreateCaseFormResponseDto>(this._baseUrl, dto);
  }

  /**
   * Gets available sensitivity categories used to classify form fields.
   * HCMI-889: these values feed the "Sensitive Fields Access" dropdown in the form
   * designer and must match the backend seed so the audit-log filter can hard-hide
   * rows whose category the user cannot see.
   *
   * GET /api/v1/cases/forms/sensitivity-categories
   */
  public getSensitivityCategories(): Observable<SensitivityCategoryResponseDto[]> {
    return this._http.get<SensitivityCategoryResponseDto[]>(`${this._baseUrl}/sensitivity-categories`);
  }

  /**
   * Gets case form versions by form ID
   * GET /api/v1/cases/forms/{formId}/versions
   */
  public getCaseFormVersionsByFormId(formId: string): Observable<FormVersionsListResponseDto> {
    return this._http.get<FormVersionsListResponseDto>(`${this._baseUrl}/${formId}/versions`);
  }

  /**
   * Gets case form version details by version ID
   * GET /api/v1/cases/forms/versions/{versionId}
   */
  public getCaseFormVersionDetailByVersionId(versionId: string): Observable<FormVersionDetailResponseDto> {
    return this._http.get<FormVersionDetailResponseDto>(`${this._baseUrl}/versions/${versionId}`);
  }

  /**
   * Creates a new draft version for a case form
   * POST /api/v1/cases/forms/versions/draft
   */
  public createCaseFormDraftVersion(formId: string): Observable<{ versionId: string }> {
    return this._http.post<{ versionId: string }>(`${this._baseUrl}/versions/draft`, { formId });
  }

  /**
   * Publishes the draft version of a case form
   * POST /api/v1/cases/forms/versions/publish
   */
  public publishDraft(formId: string): Observable<{ versionId: string }> {
    return this._http.post<{ versionId: string }>(`${this._baseUrl}/versions/publish`, { formId });
  }

  /**
   * Discards the draft version of a case form
   * DELETE /api/v1/cases/forms/versions/{formId}/draft
   */
  public discardDraft(formId: string): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/versions/${formId}/draft`);
  }

  /**
   * Gets a single case form by ID using Kendo filter
   * GET /api/v1/cases/forms with Filter query param
   */
  public getCaseFormById(id: string): Observable<CaseFormResponseDto | undefined> {
    const params = new HttpParams()
      .set('Filter', `((id~eq~${id}))`);

    return this._http.get<KendoDataSourceResult<CaseFormResponseDto>>(this._baseUrl, { params }).pipe(
      map(result => result.data.length > 0 ? result.data[0] : undefined)
    );
  }

  /**
   * Updates an existing case form
   * PUT /api/v1/cases/forms/{id}
   * NOTE: This endpoint is not implemented yet in the API
   */
  public updateCaseForm(id: string, dto: UpdateCaseFormRequestDto): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/${id}`, dto);
  }

  /**
   * Deletes a case form
   * DELETE /api/v1/cases/forms/{id}
   * NOTE: This endpoint is not implemented yet in the API
   */
  public deleteCaseForm(id: string): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/${id}`);
  }

  /**
   * Adds a field to a form version
   * POST /api/v1/cases/forms/versions/{versionId}/fields
   */
  public addField(versionId: string, dto: AddFieldRequestDto): Observable<{ id: string }> {
    return this._http.post<{ id: string }>(`${this._baseUrl}/versions/${versionId}/fields`, dto);
  }

  /**
   * Updates a field in a form version
   * PUT /api/v1/cases/forms/versions/{versionId}/fields/{fieldId}
   */
  public updateField(versionId: string, fieldId: string, dto: UpdateFieldRequestDto): Observable<{ id: string }> {
    return this._http.put<{ id: string }>(`${this._baseUrl}/versions/${versionId}/fields/${fieldId}`, dto);
  }

  /**
   * Removes a field from a form version
   * DELETE /api/v1/cases/forms/versions/{versionId}/fields/{fieldId}
   */
  public removeField(versionId: string, fieldId: string): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/versions/${versionId}/fields/${fieldId}`);
  }

  /**
   * Reorders fields in a form version
   * PUT /api/v1/cases/forms/versions/{versionId}/fields/reorder
   */
  public reorderFields(versionId: string, dto: ReorderFieldsRequestDto): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/versions/${versionId}/fields/reorder`, dto);
  }

  /**
   * Gets field definitions library with constraint types
   * GET /api/v1/cases/forms/field-definitions
   * Returns: { categories: [ { definitions: [...] } ] }
   */
  public getFieldDefinitions(): Observable<FieldDefinitionLibraryResponseDto> {
    return this._http.get<FieldDefinitionLibraryResponseDto>(`${this._baseUrl}/field-definitions`);
  }

  /**
   * Gets field types with operators and constraints
   * GET /api/v1/cases/forms/field-types
   * Returns: Complete field type information including operators and constraints
   */
  public getFieldTypes(): Observable<FieldTypeResponseDto[]> {
    return this._http.get<FieldTypeResponseDto[]>(`${this._baseUrl}/field-types`);
  }
}
